/**
 * RouteErrorFallback
 *
 * `errorElement` for the top-level data router (see App.tsx). React Router's
 * data router installs its own internal error boundary around every routed
 * element; an error thrown during render or in a committed effect (e.g. a
 * `useEffect` cleanup) is caught there, not by the plain React `<ErrorBoundary>`
 * that wraps `<RouterProvider>` from the outside. Without an `errorElement`,
 * React Router falls back to its own raw "Unexpected Application Error!" dump
 * (stack trace and all) instead of a UI a seller would ever see mid-flow.
 *
 * Mirrors ErrorBoundary's default fallback so the two crash surfaces (React
 * render errors caught by the plain boundary vs. route errors caught by the
 * router) look the same to the user.
 */

import { useEffect } from 'react';
import {
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logger } from '@/utils/logger';
import i18n from '@/lib/i18n';

export function RouteErrorFallback() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    logger.error(
      'RouteErrorFallback',
      'Uncaught error reached the router boundary',
      {
        error: isRouteErrorResponse(error)
          ? `${error.status} ${error.statusText}`
          : error instanceof Error
            ? error.message
            : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      }
    );
    // Log once per thrown error, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => navigate('/', { replace: true });
  const handleReload = () => window.location.reload();

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="max-w-md w-full mx-4 p-8 bg-paper-2 rounded-card ring-1 ring-line text-center">
        <div className="mx-auto w-16 h-16 bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] rounded-full flex items-center justify-center mb-6">
          <AlertTriangle className="w-8 h-8 text-danger" />
        </div>

        <h1 className="text-xl font-semibold text-ink mb-2">
          {i18n.t('common.unexpectedErrorTitle')}
        </h1>
        <p className="text-ink-2 mb-6">
          {i18n.t('common.unexpectedErrorBody')}
        </p>

        {import.meta.env.DEV && (
          <div className="mb-6 p-3 bg-paper-2 rounded-control text-left">
            <p className="text-xs font-mono text-danger break-all">
              {isRouteErrorResponse(error)
                ? `${error.status} ${error.statusText}`
                : error instanceof Error
                  ? error.message
                  : String(error)}
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-fg rounded-control hover:brightness-[0.92] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {i18n.t('common.retry')}
          </button>
          <button
            onClick={handleReload}
            className="inline-flex items-center gap-2 px-4 py-2 border border-line text-ink-2 rounded-control hover:bg-paper transition-colors"
          >
            {i18n.t('common.reloadPage')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RouteErrorFallback;
